import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-4" };
const _hoisted_2 = { class: "border border-(--border-border-base-soft) rounded-(--border-border-radius-rounded-l) shadow-s bg-white flex flex-col divide-y divide-(--border-border-base-soft)" };
const _hoisted_3 = { class: "w-full sm:flex-2" };
const _hoisted_4 = { class: "w-full sm:flex-2" };
const _hoisted_5 = { class: "w-full sm:flex-3 flex flex-col gap-y-3 sm:flex-row sm:gap-x-5 sm:gap-y-0" };
const _hoisted_6 = { class: "w-full sm:flex-1" };
const _hoisted_7 = { class: "w-full sm:flex-1" };
const _hoisted_8 = { class: "w-full sm:contents flex items-center" };
const _hoisted_9 = { class: "shrink-0 min-w-[7rem] sm:mt-7" };
const _hoisted_10 = { class: "shrink-0 ml-auto sm:mt-6" };
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Badge, Button, DatePickerInput, SelectInput, TextInput } from "@eudonet/opal";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useProfileStore } from "@/modules/settings/stores/useProfileStore";
import { toDatePickerFormat, isoToDisplayDate, displayDateToIso } from "@/shared/utils/date-formatting";
import { useRelationFunctionOptions } from "../../composables/useRelationFunctionOptions";
export default /*@__PURE__*/ _defineComponent({
    __name: 'RelationFunctionRows',
    props: /*@__PURE__*/ _mergeModels({
        requireTitle: { type: Boolean }
    }, {
        "modelValue": { required: true },
        "modelModifiers": {},
    }),
    emits: ["update:modelValue"],
    setup(__props, { expose: __expose }) {
        const rows = _useModel(__props, "modelValue");
        const props = __props;
        const { locale, t } = useI18n();
        const { canEditOphub: canEdit } = usePermissions();
        const profileStore = useProfileStore();
        const { functionOptions } = useRelationFunctionOptions();
        const dateFormat = computed(() => toDatePickerFormat(profileStore.profile?.dateFormat, locale.value));
        // ─── Touched tracking ──────────────────────────────────────────────────────────
        const touchedRows = ref({});
        function touchField(rowId, field) {
            if (!touchedRows.value[rowId])
                touchedRows.value[rowId] = {};
            touchedRows.value[rowId][field] = true;
        }
        // ─── Validity (single source of truth, consumed by parent modals via defineExpose) ──
        function rowHasError(row) {
            if (!row.function)
                return true;
            if (props.requireTitle && !row.title.trim())
                return true;
            if (row.title.length > 255)
                return true;
            if (!row.start)
                return true;
            if (row.end) {
                const startIso = displayDateToIso(row.start, profileStore.profile?.dateFormat, locale.value);
                const endIso = displayDateToIso(row.end, profileStore.profile?.dateFormat, locale.value);
                if (startIso && endIso && endIso < startIso)
                    return true;
            }
            return false;
        }
        const hasErrors = computed(() => rows.value.some(rowHasError));
        __expose({ get hasErrors() { return hasErrors.value; } });
        // ─── Inline errors (touch-gated messages; validity rules stay in rowHasError) ──
        function titleError(row) {
            if (!touchedRows.value[row._id]?.title)
                return undefined;
            if (props.requireTitle && !row.title.trim())
                return t("contact.detail.relations.edit.errors.titleRequired");
            if (row.title.length > 255)
                return t("contact.detail.relations.errors.functionTitleTooLong");
            return undefined;
        }
        function functionError(row) {
            if (!touchedRows.value[row._id]?.function)
                return undefined;
            if (!row.function)
                return t("contact.detail.relations.errors.functionRequired");
            return undefined;
        }
        function startError(row) {
            if (!touchedRows.value[row._id]?.start)
                return undefined;
            if (!row.start)
                return t("contact.detail.relations.errors.functionStartDateRequired");
            return undefined;
        }
        function endError(row) {
            if (!touchedRows.value[row._id]?.end)
                return undefined;
            if (!row.end)
                return undefined;
            const startIso = displayDateToIso(row.start, profileStore.profile?.dateFormat, locale.value);
            const endIso = displayDateToIso(row.end, profileStore.profile?.dateFormat, locale.value);
            if (startIso && endIso && endIso < startIso)
                return t("contact.detail.relations.errors.invalidDateRange");
            return undefined;
        }
        // ─── Row active status ─────────────────────────────────────────────────────────
        // "En cours" = endDate empty or not yet passed; "Archivée" = endDate in the past
        // Visual only — no stored status field (Path D batch handles actual archival)
        function isRowActive(row) {
            if (!row.end)
                return true;
            const parsed = displayDateToIso(row.end, profileStore.profile?.dateFormat, locale.value);
            if (!parsed)
                return true;
            return parsed >= new Date().toISOString().slice(0, 10);
        }
        // ─── Row management ────────────────────────────────────────────────────────────
        function addRow() {
            const todayIso = new Date().toISOString().slice(0, 10);
            rows.value = [
                ...rows.value,
                {
                    _id: crypto.randomUUID(),
                    title: "",
                    function: "",
                    start: isoToDisplayDate(todayIso, profileStore.profile?.dateFormat, locale.value),
                    end: "",
                },
            ];
        }
        function removeRow(index) {
            if (rows.value.length > 1) {
                const removed = rows.value[index];
                rows.value = rows.value.filter((_, i) => i !== index);
                const updated = { ...touchedRows.value };
                delete updated[removed._id];
                touchedRows.value = updated;
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row, index) => {
                        return (_openBlock(), _createElementBlock("div", {
                            key: row._id,
                            class: "flex flex-wrap gap-x-5 gap-y-3 items-start px-5 py-5"
                        }, [
                            _createElementVNode("div", _hoisted_3, [
                                _createVNode(_unref(TextInput), {
                                    modelValue: row.title,
                                    "onUpdate:modelValue": ($event) => ((row.title) = $event),
                                    label: _ctx.$t('contact.detail.relations.create.fields.title.label'),
                                    placeholder: _ctx.$t('contact.detail.relations.create.fields.title.placeholder'),
                                    required: __props.requireTitle,
                                    disabled: !_unref(canEdit),
                                    error: titleError(row),
                                    "full-width": "",
                                    onBlur: ($event) => (touchField(row._id, 'title'))
                                }, null, 8 /* PROPS */, ["modelValue", "onUpdate:modelValue", "label", "placeholder", "required", "disabled", "error", "onBlur"])
                            ]),
                            _createElementVNode("div", _hoisted_4, [
                                _createVNode(_unref(SelectInput), {
                                    "model-value": row.function,
                                    label: _ctx.$t('contact.detail.relations.create.fields.function.label'),
                                    placeholder: _ctx.$t('contact.detail.relations.create.fields.function.placeholder'),
                                    options: _unref(functionOptions),
                                    required: true,
                                    disabled: !_unref(canEdit),
                                    error: functionError(row),
                                    "full-width": "",
                                    "onUpdate:modelValue": (val) => { row.function = val; touchField(row._id, 'function'); }
                                }, null, 8 /* PROPS */, ["model-value", "label", "placeholder", "options", "disabled", "error", "onUpdate:modelValue"])
                            ]),
                            _createElementVNode("div", _hoisted_5, [
                                _createElementVNode("div", _hoisted_6, [
                                    _createVNode(_unref(DatePickerInput), {
                                        "model-value": row.start,
                                        label: _ctx.$t('contact.detail.relations.create.fields.start.label'),
                                        format: dateFormat.value,
                                        language: _unref(locale),
                                        "week-start": 1,
                                        required: true,
                                        disabled: !_unref(canEdit),
                                        error: startError(row),
                                        "full-width": "",
                                        "onUpdate:modelValue": (val) => { row.start = val; touchField(row._id, 'start'); }
                                    }, null, 8 /* PROPS */, ["model-value", "label", "format", "language", "disabled", "error", "onUpdate:modelValue"])
                                ]),
                                _createElementVNode("div", _hoisted_7, [
                                    _createVNode(_unref(DatePickerInput), {
                                        "model-value": row.end,
                                        label: _ctx.$t('contact.detail.relations.create.fields.end.label'),
                                        format: dateFormat.value,
                                        language: _unref(locale),
                                        "week-start": 1,
                                        "min-date": row.start || undefined,
                                        disabled: !_unref(canEdit),
                                        error: endError(row),
                                        "full-width": "",
                                        "onUpdate:modelValue": (val) => { row.end = val; touchField(row._id, 'end'); }
                                    }, null, 8 /* PROPS */, ["model-value", "label", "format", "language", "min-date", "disabled", "error", "onUpdate:modelValue"])
                                ])
                            ]),
                            _createElementVNode("div", _hoisted_8, [
                                _createElementVNode("div", _hoisted_9, [
                                    _createVNode(_unref(Badge), {
                                        type: "with-dot",
                                        theme: isRowActive(row) ? 'green' : 'neutral',
                                        label: isRowActive(row)
                                            ? _ctx.$t('contact.detail.relations.create.fields.status.active')
                                            : _ctx.$t('contact.detail.relations.create.fields.status.inactive'),
                                        "show-close-icon": false,
                                        size: "sm"
                                    }, null, 8 /* PROPS */, ["theme", "label"])
                                ]),
                                _createElementVNode("div", _hoisted_10, [
                                    _createVNode(_unref(Button), {
                                        variant: "secondary",
                                        size: "sm",
                                        "icon-only": "",
                                        "icon-left": "ri-delete-bin-line",
                                        "aria-label": _ctx.$t('common.delete'),
                                        disabled: !_unref(canEdit) || rows.value.length === 1,
                                        onClick: ($event) => (removeRow(index))
                                    }, null, 8 /* PROPS */, ["aria-label", "disabled", "onClick"])
                                ])
                            ])
                        ]));
                    }), 128 /* KEYED_FRAGMENT */))
                ]),
                _createVNode(_unref(Button), {
                    variant: "secondary",
                    size: "sm",
                    "icon-left": "ri-add-line",
                    class: "self-start",
                    label: _ctx.$t('contact.detail.relations.create.addFunction'),
                    disabled: !_unref(canEdit),
                    onClick: addRow
                }, null, 8 /* PROPS */, ["label", "disabled"])
            ]));
        };
    }
});
